
import { useState, useCallback, useRef, useEffect } from 'react';
// FIX: Removed LiveSession from import as it's not exported from the package.
import { GoogleGenAI, LiveServerMessage, Modality, Blob, FunctionCall } from '@google/genai';
import { decode, decodeAudioData, encode } from '../services/audioUtils';
import { getGeminiFunctions } from '../services/geminiService';

// FIX: Added a local interface for the live session to provide type safety.
interface LiveSession {
    close: () => void;
    sendToolResponse: (response: { functionResponses: any; }) => void;
}

export const useGeminiLive = () => {
    const [isListening, setIsListening] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [userTranscript, setUserTranscript] = useState('');
    const [aiTranscript, setAiTranscript] = useState('');
    const [permissionState, setPermissionState] = useState<PermissionState | 'prompt' | 'denied' | 'granted' | null>(null);
    const [functionCallQueue, setFunctionCallQueue] = useState<FunctionCall[]>([]);

    const sessionRef = useRef<LiveSession | null>(null);
    const inputAudioContextRef = useRef<AudioContext | null>(null);
    const outputAudioContextRef = useRef<AudioContext | null>(null);
    const inputNodeRef = useRef<GainNode | null>(null);
    const outputNodeRef = useRef<GainNode | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
    
    const userTranscriptRef = useRef('');
    const aiTranscriptRef = useRef('');
    const audioQueueRef = useRef<{ buffer: AudioBuffer, startTime: number }[]>([]);
    const nextStartTimeRef = useRef(0);
    const isPlayingRef = useRef(false);

    useEffect(() => {
        const checkPermission = async () => {
            if (navigator.permissions) {
                const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
                setPermissionState(permissionStatus.state);
                permissionStatus.onchange = () => {
                    setPermissionState(permissionStatus.state);
                };
            }
        };
        checkPermission();
    }, []);

    const playAudioQueue = useCallback(() => {
        if (isPlayingRef.current || audioQueueRef.current.length === 0 || !outputAudioContextRef.current) return;
        
        isPlayingRef.current = true;
        setIsSpeaking(true);

        const playNext = () => {
            if (audioQueueRef.current.length === 0) {
                isPlayingRef.current = false;
                setIsSpeaking(false);
                setAiTranscript(''); // Clear AI transcript after it finishes speaking
                return;
            }

            const { buffer } = audioQueueRef.current.shift()!;
            const source = outputAudioContextRef.current!.createBufferSource();
            source.buffer = buffer;
            source.connect(outputNodeRef.current!);
            source.onended = playNext;
            
            const startAt = Math.max(nextStartTimeRef.current, outputAudioContextRef.current!.currentTime);
            source.start(startAt);
            nextStartTimeRef.current = startAt + buffer.duration;
        };

        playNext();
    }, []);

    const startSession = useCallback(async () => {
        if (isListening) return;

        // FIX: Create/resume AudioContexts immediately on user gesture to satisfy mobile browser policies.
        if (!outputAudioContextRef.current) {
            outputAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
            outputNodeRef.current = outputAudioContextRef.current.createGain();
            outputNodeRef.current.connect(outputAudioContextRef.current.destination);
        }
        if (outputAudioContextRef.current.state === 'suspended') {
            await outputAudioContextRef.current.resume();
        }

        if (!inputAudioContextRef.current) {
            inputAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
            inputNodeRef.current = inputAudioContextRef.current.createGain();
        }
        if (inputAudioContextRef.current.state === 'suspended') {
            await inputAudioContextRef.current.resume();
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;
            setPermissionState('granted');
            setIsListening(true);
            
            userTranscriptRef.current = '';
            aiTranscriptRef.current = '';
            setUserTranscript('');
            setAiTranscript('');

            const ai = new GoogleGenAI({ apiKey: process.env.API_KEY! });

            const sessionPromise = ai.live.connect({
                model: 'gemini-2.5-flash-native-audio-preview-09-2025',
                callbacks: {
                    onopen: () => {
                        // Contexts are already created. Now just connect the media stream source.
                        const source = inputAudioContextRef.current!.createMediaStreamSource(mediaStreamRef.current!);
                        scriptProcessorRef.current = inputAudioContextRef.current!.createScriptProcessor(4096, 1, 1);
                        scriptProcessorRef.current.onaudioprocess = (audioProcessingEvent) => {
                            const inputData = audioProcessingEvent.inputBuffer.getChannelData(0);
                            const pcmBlob: Blob = {
                                data: encode(new Uint8Array(new Int16Array(inputData.map(x => x * 32768)).buffer)),
                                mimeType: 'audio/pcm;rate=16000',
                            };
                            sessionPromise.then(session => session.sendRealtimeInput({ media: pcmBlob }));
                        };
                        source.connect(scriptProcessorRef.current);
                        scriptProcessorRef.current.connect(inputAudioContextRef.current!.destination);
                    },
                    onmessage: async (message: LiveServerMessage) => {
                       setIsProcessing(false);
                       if (message.serverContent?.inputTranscription) {
                            const text = message.serverContent.inputTranscription.text;
                            userTranscriptRef.current += text;
                            setUserTranscript(userTranscriptRef.current);
                       }
                       if (message.serverContent?.outputTranscription) {
                            const text = message.serverContent.outputTranscription.text;
                            aiTranscriptRef.current += text;
                            setAiTranscript(aiTranscriptRef.current);
                       }
                       if (message.serverContent?.turnComplete) {
                           userTranscriptRef.current = '';
                           aiTranscriptRef.current = '';
                       }
                       const base64Audio = message.serverContent?.modelTurn?.parts[0]?.inlineData?.data;
                       if (base64Audio && outputAudioContextRef.current) {
                           const audioBuffer = await decodeAudioData(decode(base64Audio), outputAudioContextRef.current, 24000, 1);
                           audioQueueRef.current.push({ buffer: audioBuffer, startTime: nextStartTimeRef.current });
                           playAudioQueue();
                       }

                       if(message.toolCall) {
                           setIsProcessing(true);
                           setFunctionCallQueue(prev => [...prev, ...message.toolCall.functionCalls]);
                       }
                    },
                    onerror: (e: ErrorEvent) => {
                        console.error('Session error:', e);
                        setIsListening(false);
                    },
                    onclose: (e: CloseEvent) => {
                        setIsListening(false);
                    },
                },
                config: {
                    responseModalities: [Modality.AUDIO],
                    inputAudioTranscription: {},
                    outputAudioTranscription: {},
                    tools: [{ functionDeclarations: getGeminiFunctions() }],
                    systemInstruction: "You are a helpful and compassionate prayer assistant. Be encouraging and gentle. When executing commands, provide brief, natural-sounding confirmation. For example, 'Okay, adding a prayer for...' or 'Got it. Here are the pending prayers.'",
                }
            });

            sessionRef.current = await sessionPromise;

        } catch (error) {
            console.error("Failed to start session:", error);
            if (error instanceof DOMException && error.name === "NotAllowedError") {
                setPermissionState('denied');
            }
            setIsListening(false);
        }
    }, [isListening, playAudioQueue]);

    const closeSession = useCallback(() => {
        if (!isListening) return;

        sessionRef.current?.close();
        sessionRef.current = null;
        
        scriptProcessorRef.current?.disconnect();
        scriptProcessorRef.current = null;

        mediaStreamRef.current?.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;

        inputAudioContextRef.current?.close();
        outputAudioContextRef.current?.close();
        inputAudioContextRef.current = null;
        outputAudioContextRef.current = null;
        
        audioQueueRef.current = [];
        nextStartTimeRef.current = 0;
        isPlayingRef.current = false;
        
        setIsListening(false);
        setIsSpeaking(false);
        setIsProcessing(false);
    }, [isListening]);
    
    const sendToolResponse = useCallback((response: { functionResponses: any; }) => {
        if (sessionRef.current) {
            sessionRef.current.sendToolResponse(response);
        }
    }, []);

    const addBase64AudioToQueue = useCallback(async (base64Audio: string) => {
        if (!outputAudioContextRef.current) {
            console.error("Output audio context not available");
            return;
        }
        try {
            const audioBuffer = await decodeAudioData(decode(base64Audio), outputAudioContextRef.current, 24000, 1);
            audioQueueRef.current.push({ buffer: audioBuffer, startTime: nextStartTimeRef.current });
            playAudioQueue();
        } catch (error) {
            console.error("Failed to decode and queue audio:", error);
        }
    }, [playAudioQueue]);


    const toggleListening = useCallback(() => {
        if (isListening) {
            closeSession();
        } else {
            startSession();
        }
    }, [isListening, startSession, closeSession]);

    return {
        isListening,
        isSpeaking,
        isProcessing,
        userTranscript,
        aiTranscript,
        toggleListening,
        permissionState,
        functionCallQueue,
        setFunctionCallQueue,
        sendToolResponse,
        addBase64AudioToQueue
    };
};